import React, { useEffect } from "react";
import styles from "./style.module.scss";
import { useQuery } from "@apollo/client";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state";
import { GET_RECENT_TYRE_SNAPSHOTS } from "../../../../utils/graphql/queries/TPMSQueries";

export function CacheTableButton({ onOpen }: { onOpen: () => void }) {
  return (
    <button className={styles.tableButton} type="button" onClick={onOpen}>
      Table
    </button>
  );
}

export function CacheTableContent() {
  const { data } = useQuery(GET_RECENT_TYRE_SNAPSHOTS, {
    fetchPolicy: "network-only",
  });

  const tpmsConfig = useSelector((state: RootState) => state.tpms.tpmsConfig);

  useEffect(() => {
    // For logs
  }, [data, tpmsConfig]);

  type TyreFrame = {
    TAir?: number;
    [key: string]: unknown;
  };

  type TyreSnapshot = {
    ts: string;
    frames?: Record<string, TyreFrame>;
  };

  type SensorTyrePair = {
    sensorId: string;
    tyreId: string;
    carId: string;
    frame: TyreFrame | undefined;
  };
  const carIdToSnapshots: Record<
    string,
    Array<{ snapshot: TyreSnapshot; sensorTyrePairs: SensorTyrePair[] }>
  > = {};

  data?.getRecentTyreSnapshots?.forEach((snapshot: TyreSnapshot) => {
    const tyreIds = snapshot.frames ? Object.keys(snapshot.frames) : [];
    const sensorTyrePairs = tyreIds.map((sensorId) => {
      type TyreConfig = {
        sensorId: string;
        tyreId: string;
        carId?: string;
      };
      const tyreObj = tpmsConfig?.tyres?.find(
        (tyre: TyreConfig) => tyre.sensorId === sensorId,
      );
      return {
        sensorId,
        tyreId: tyreObj?.tyreId ?? "",
        carId: tyreObj?.carId ?? "",
        frame: snapshot.frames ? snapshot.frames[sensorId] : undefined,
      };
    });

    const carIds = Array.from(
      new Set(sensorTyrePairs.map((pair) => pair.carId)),
    );
    carIds.forEach((carId) => {
      if (!carIdToSnapshots[carId]) carIdToSnapshots[carId] = [];
      carIdToSnapshots[carId].push({
        snapshot,
        sensorTyrePairs: sensorTyrePairs.filter((pair) => pair.carId === carId),
      });
    });
  });

  return (
    <div className={styles.tableScrollContainer}>
      <div className={styles.multiTableRow}>
        {Object.entries(carIdToSnapshots).map(([carId, snapshots]) => (
          <div key={carId} style={{ minWidth: 340 }}>
            <h3>Car: {carId}</h3>
            <table className={styles.blankTable}>
              <thead>
                <tr>
                  <th>Snapshot Time</th>
                  <th>Tyre ID</th>
                  <th>Sensor ID</th>
                  <th>Pressure (bar)</th>
                  <th>Tyre Temp (°C)</th>
                  <th>TAir (°C)</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map(({ snapshot, sensorTyrePairs }) => {
                  const tsNum = Number(snapshot.ts);
                  const parsedDate = new Date(
                    !isNaN(tsNum) ? tsNum : snapshot.ts,
                  );
                  const snapshotTime = !isNaN(parsedDate.getTime())
                    ? parsedDate.toLocaleTimeString([], { hour12: false })
                    : snapshot.ts;

                  sensorTyrePairs.sort((a, b) => {
                    const aIsF = a.tyreId.startsWith("F");
                    const bIsF = b.tyreId.startsWith("F");
                    const aIsR = a.tyreId.startsWith("R");
                    const bIsR = b.tyreId.startsWith("R");
                    if (aIsF && !bIsF) return -1;
                    if (!aIsF && bIsF) return 1;
                    if (aIsR && !bIsR) return 1;
                    if (!aIsR && bIsR) return -1;
                    // If both are F or both are R, sort by number
                    const aNum = parseInt(a.tyreId.slice(1), 10);
                    const bNum = parseInt(b.tyreId.slice(1), 10);
                    if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
                    return 0;
                  });
                  return sensorTyrePairs.length > 0 ? (
                    sensorTyrePairs.map(({ sensorId, tyreId, frame }, idx) => {
                      const tAir =
                        frame && typeof frame === "object" && "TAir" in frame
                          ? frame.TAir
                          : "";
                      const pAbs = frame?.pAbs;
                      const pAtm = tpmsConfig?.pAtm ?? 1;
                      const pressureBar =
                        typeof pAbs === "number"
                          ? (pAbs / 1000 - pAtm).toFixed(3)
                          : pAbs !== undefined
                          ? (Number(pAbs) / 1000 - pAtm).toFixed(3)
                          : "";
                      const tTyre =
                        typeof frame?.TIR2 === "number" &&
                        typeof frame?.TIR3 === "number" &&
                        typeof frame?.TIR4 === "number"
                          ? (
                              (frame.TIR2 + frame.TIR3 + frame.TIR4) /
                              3
                            ).toFixed(1)
                          : typeof frame?.TIR3 === "number"
                          ? frame.TIR3.toFixed(1)
                          : "";

                      return (
                        <tr
                          key={`${snapshot.ts}-${sensorId}`}
                          className={
                            idx === 0 ? styles.timestampRow : undefined
                          }
                        >
                          {idx === 0 && (
                            <td rowSpan={sensorTyrePairs.length}>
                              {snapshotTime}
                            </td>
                          )}
                          <td>{tyreId}</td>
                          <td>{sensorId}</td>
                          <td>{pressureBar}</td>
                          <td>{tTyre}</td>
                          <td>
                            {tAir !== undefined && tAir !== null ? tAir : ""}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr key={snapshot.ts} className={styles.timestampRow}>
                      <td>{snapshotTime}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
